import { Firmware, FirmwareQueryParam, FirmwareUploadParam } from '/@/types/device-firmware'
import request, { CommonListResponse, IListWorkspaceResponse, IPage, IWorkspaceResponse } from '/@/api/http/request'
import { Device } from '/@/types/device'

const HTTP_PREFIX = '/manage/api/v1'

// login
export interface LoginBody {
 username: string,
 password: string,
 flag: number,
}
export interface BindBody {
  device_sn: string,
  user_id: string,
  workspace_id: string,
  domain?: string
}
export interface HmsQueryBody {
  sns: string[],
  children_sn: string,
  device_sn: string,
  language: string,
  level: number | string,
  begin_time: number,
  end_time: number,
  message: string,
  domain: number,
}

export const login = async function (body: LoginBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/login`
  const result = await request.post(url, body)
  return result.data
}

// Refresh Token
export const refreshToken = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/token/refresh`
  const result = await request.post(url, body)
  return result.data
}

// Get Platform Info
export const getPlatformInfo = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/current`
  const result = await request.get(url)
  return result.data
}

// Get User Info
export const getUserInfo = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/current`
  const result = await request.get(url)
  return result.data
}

// Get Device Topo
export const getDeviceTopo = async function (workspace_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices`
  const result = await request.get(url)
  return result.data
}

// Get Livestream Capacity
export const getLiveCapacity = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/capacity`
  const result = await request.get(url, body)
  return result.data
}

// Start Livestream
export const startLivestream = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/start`
  const result = await request.post(url, body)
  return result.data
}

// Stop Livestream
export const stopLivestream = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/stop`
  const result = await request.post(url, body)
  return result.data
}

// Get active streams (persisted in Redis — for late joiners)
export const getActiveStreams = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams`
  const result = await request.get(url)
  return result.data
}

// Admin: force-stop ALL active livestreams of the workspace (kill switch for ghost/blocked streams)
export const stopAllLivestreams = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/stop-all`
  const result = await request.post(url, {})
  return result.data
}
// Update Quality
export const setLivestreamQuality = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/update`
  const result = await request.post(url, body)
  return result.data
}

// ── Liens de partage temporaires (admin) ──────────────────────────────────────
// Créer un lien public pour un live actif. body: { video_id, duration_seconds }
export const createLiveShare = async function (body: { video_id: string, duration_seconds: number }): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/share`
  const result = await request.post(url, body)
  return result.data
}

// Lister les liens de partage actifs du workspace
export const listLiveShares = async function (): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/shares`
  const result = await request.get(url)
  return result.data
}

// Révoquer un lien de partage avant expiration
export const revokeLiveShare = async function (uuid: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/share/${uuid}`
  const result = await request.delete(url)
  return result.data
}

// Résoudre un lien public (page partage, SANS authentification).
// Utilise axios brut pour éviter l'injection du token et la redirection 401 du helper.
export const resolveLiveShare = async function (uuid: string): Promise<IWorkspaceResponse<any>> {
  const { default: axios } = await import('axios')
  const { CURRENT_CONFIG } = await import('/@/api/http/config')
  const result = await axios.get(`${CURRENT_CONFIG.baseURL}${HTTP_PREFIX}/share/${uuid}`)
  return result.data
}

export const getAllUsersInfo = async function (wid: string, body: IPage): Promise<CommonListResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users?&page=${body.page}&page_size=${body.page_size}`
  const result = await request.get(url)
  return result.data
}

export const updateUserInfo = async function (wid: string, user_id: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}`
  const result = await request.put(url, body)
  return result.data
}

export const createUserReq = async function (wid: string, body: { username: string; password: string; user_type: number; user_color?: string }): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users`
  const result = await request.post(url, body)
  return result.data
}

export const deleteUserReq = async function (wid: string, user_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}`
  const result = await request.delete(url)
  return result.data
}

export const uploadAvatarReq = async function (wid: string, user_id: string, file: File): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}/avatar`
  const form = new FormData()
  form.append('file', file)
  const result = await request.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })
  return result.data
}

export const deleteAvatarReq = async function (wid: string, user_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}/avatar`
  const result = await request.delete(url)
  return result.data
}

export const bindDevice = async function (body: BindBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${body.device_sn}/binding`
  const result = await request.post(url, body)
  return result.data
}

export const unbindDevice = async function (device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${device_sn}/unbinding`
  const result = await request.delete(url)
  return result.data
}

export const getDeviceBySn = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.get(url)
  return result.data
}

/**
 * 获取绑定设备信息
 * @param workspace_id
 * @param body
 * @param domain
 * @returns
 */
export const getBindingDevices = async function (workspace_id: string, body: IPage, domain: number): Promise<IListWorkspaceResponse<Device>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/bound?&page=${body.page}&page_size=${body.page_size}&domain=${domain}`
  const result = await request.get(url)
  return result.data
}

export const updateDevice = async function (body: {}, workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.put(url, body)
  return result.data
}

export const getUnreadDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.get(url)
  return result.data
}

export const updateDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.put(url)
  return result.data
}

export const getDeviceHms = async function (body: HmsQueryBody, workspace_id: string, pagination: IPage): Promise<IListWorkspaceResponse<any>> {
  let url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms?page=${pagination.page}&page_size=${pagination.page_size}` +
    `&level=${body.level ?? ''}&begin_time=${body.begin_time ?? ''}&end_time=${body.end_time ?? ''}&message=${body.message ?? ''}&language=${body.language}`
  body.sns.forEach((sn: string) => {
    if (sn !== '') {
      url = url.concat(`&device_sn=${sn}`)
    }
  })
  const result = await request.get(url)
  return result.data
}

export const changeLivestreamLens = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/switch`
  const result = await request.post(url, body)
  return result.data
}

export const getFirmwares = async function (workspace_id: string, page: IPage, body: FirmwareQueryParam): Promise<IListWorkspaceResponse<Firmware>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/firmwares?page=${page.page}&page_size=${page.page_size}` +
    `&device_name=${body.device_name}&product_version=${body.product_version}&status=${body.firmware_status ?? ''}`
  const result = await request.get(url)
  return result.data
}

export const importFirmareFile = async function (workspaceId: string, param: FormData): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/file/upload`
  const result = await request.post(url, param)
  return result.data
}

export const changeFirmareStatus = async function (workspaceId: string, firmwareId: string, param: {status: boolean}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/${firmwareId}`
  const result = await request.put(url, param)
  return result.data
}

export interface WorkspaceInfo {
  workspace_id: string
  workspace_name: string
  workspace_desc: string
  platform_name: string
  bind_code: string
  create_time: number
  created_by?: string
  device_count?: number
  archive_time?: number | null
}

export const listWorkspacesReq = async (): Promise<any> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces`)
  return result.data
}

export const createWorkspaceReq = async (name: string, createdBy?: string): Promise<any> => {
  const body: Record<string, string> = { name }
  if (createdBy) body.created_by = createdBy
  const result = await request.post(`${HTTP_PREFIX}/workspaces`, body)
  return result.data
}

// Liste tous les comptes d'un type donné (VGRID = 4), toutes workspaces confondues.
export const listUsersByTypeReq = async (userType: number): Promise<any> => {
  const result = await request.get(`${HTTP_PREFIX}/users/by-type/${userType}`)
  return result.data
}

export const updateWorkspaceReq = async (workspaceId: string, name: string): Promise<any> => {
  const result = await request.put(`${HTTP_PREFIX}/workspaces/${workspaceId}`, { name })
  return result.data
}

export const deleteWorkspaceReq = async (workspaceId: string): Promise<any> => {
  const result = await request.delete(`${HTTP_PREFIX}/workspaces/${workspaceId}`)
  return result.data
}

// Archive une intervention (workspace) : pose archive_time côté serveur.
export const archiveWorkspaceReq = async (workspaceId: string): Promise<any> => {
  const result = await request.put(`${HTTP_PREFIX}/workspaces/${workspaceId}/archive`)
  return result.data
}

// Désarchive une intervention : efface archive_time côté serveur.
export const unarchiveWorkspaceReq = async (workspaceId: string): Promise<any> => {
  const result = await request.delete(`${HTTP_PREFIX}/workspaces/${workspaceId}/archive`)
  return result.data
}

// ── Workspace access (waiting room) ─────────────────────────────────────────

export const getAccessibleWorkspaces = async (): Promise<IWorkspaceResponse<any>> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces/accessible`)
  return result.data
}

export const getMyAccess = async (): Promise<IWorkspaceResponse<any>> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces/my-access`)
  return result.data
}

export const requestWorkspaceAccess = async (workspaceId: string): Promise<IWorkspaceResponse<any>> => {
  const result = await request.post(`${HTTP_PREFIX}/workspaces/${workspaceId}/access/request`)
  return result.data
}

export const getWorkspaceAccessRequests = async (workspaceId: string): Promise<IWorkspaceResponse<any>> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces/${workspaceId}/access`)
  return result.data
}

export const getAllPendingAccessRequests = async (): Promise<IWorkspaceResponse<any>> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces/access/pending`)
  return result.data
}

export const approveWorkspaceAccess = async (workspaceId: string, userId: string, role: number): Promise<IWorkspaceResponse<any>> => {
  const result = await request.put(`${HTTP_PREFIX}/workspaces/${workspaceId}/access/${userId}/approve`, { role })
  return result.data
}

export const removeWorkspaceAccess = async (workspaceId: string, userId: string): Promise<IWorkspaceResponse<any>> => {
  const result = await request.delete(`${HTTP_PREFIX}/workspaces/${workspaceId}/access/${userId}`)
  return result.data
}

export const cancelOwnAccessRequest = async (workspaceId: string): Promise<IWorkspaceResponse<any>> => {
  const result = await request.delete(`${HTTP_PREFIX}/workspaces/${workspaceId}/access/request`)
  return result.data
}

export const getWorkspaceMembersReq = async (workspaceId: string): Promise<IWorkspaceResponse<any>> => {
  const result = await request.get(`${HTTP_PREFIX}/workspaces/${workspaceId}/members`)
  return result.data
}

export const joinWorkspacePresence = async (workspaceId: string): Promise<void> => {
  await request.put(`${HTTP_PREFIX}/workspaces/${workspaceId}/presence`).catch(() => {})
}

export const leaveWorkspacePresence = async (workspaceId: string): Promise<void> => {
  await request.delete(`${HTTP_PREFIX}/workspaces/${workspaceId}/presence`).catch(() => {})
}

export const switchWorkspaceToken = async (workspaceId: string): Promise<string | null> => {
  try {
    const result = await request.post(`${HTTP_PREFIX}/workspaces/${workspaceId}/switch-token`)
    return result.data?.data?.access_token ?? null
  } catch {
    return null
  }
}

// Notify all web clients (map_group_refresh) that the RC has connected and annotations may have changed
export const notifyMapSync = async (workspaceId: string): Promise<void> => {
  await request.post(`/map/api/v1/workspaces/${workspaceId}/map-sync-notify`).catch(() => {})
}
